import { useCallback, useEffect, useState, useRef } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@antimatterhq/ui/components/accordion';
import { Text } from '@antimatterhq/ui/components/typography';
import { useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { useDatasourceUpgrade } from '@/contexts/DatasourceUpgradeContext';
import {
  BronzeReview,
  GoldReview,
  PreTransformReview,
  SilverReview,
  SourceSelectionReview,
} from '@/components/dataSources/review/';
import { cn } from '@/lib/utils';
import { DatasourceUpgradeDrawer } from '@/components/dataSources/upgrade/drawer/DatasourceUpgradeDrawer';
import { useOpenSidebar } from '@/hooks/iframe/useOpenSidebar';
import { UnsavedChangesDialog } from '@/components/dialog/unsavedChanges/UnsavedChangesDialog';
import { useDatasourceUpgradeToast } from '@/hooks/components/useDatasourceUpgradeToast';

const sections = [
  {
    step: 'getting-started',
    title: 'Source selection',
    cssClass: '',
  },
  {
    step: 'bronze',
    title: 'Bronze table',
    cssClass: 'border-bronze',
  },
  {
    step: 'silver-pre-transform',
    title: 'Silver pre-transform',
    cssClass: 'border-silver',
  },
  {
    step: 'silver',
    title: 'Silver',
    cssClass: 'border-silver',
  },
  {
    step: 'gold',
    title: 'Gold',
    cssClass: 'border-gold',
  },
] as const;

export const DatasourceUpgradeReview = ({
  setDrawerHeight,
}: {
  setDrawerHeight: (height: number) => void;
}) => {
  const {
    datasource,
    upgradeDiff,
    presetUpgradeVersion,
    abort,
    upgradeStatus,
    save,
    isSaving,
    isPublishing,
    presetName,
  } = useDatasourceUpgrade();
  const { open } = useOpenSidebar();
  const navigate = useNavigate();
  const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] =
    useState(false);
  const pendingSave = useRef(false);
  const { showDatasourceUpgradeInProgress } = useDatasourceUpgradeToast();

  useEffect(() => {
    open(false);
  }, [open]);

  const handleOnDiscard = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
    abort({
      onSuccess: () => {
        navigate('/configure/data-sources');
      },
    });
  }, [abort, navigate]);

  const handleOnConfirm = useCallback(() => {
    setUnsavedChangesDialogOpen(false);
    navigate('/configure/data-sources');
    showDatasourceUpgradeInProgress({
      presetName,
      presetVersion: presetUpgradeVersion,
      datasource,
    });
  }, [
    datasource,
    navigate,
    presetName,
    presetUpgradeVersion,
    showDatasourceUpgradeInProgress,
  ]);

  // Debounced save effect
  const debouncedSave = useDebouncedCallback(() => {
    if (isPublishing) {
      pendingSave.current = false;
      return;
    }

    if (!isSaving) {
      save();
    } else {
      pendingSave.current = true;
    }
  }, 500);

  useEffect(() => {
    debouncedSave();
    return () => {
      debouncedSave.cancel();
    };
  }, [upgradeStatus, datasource, debouncedSave]);

  // Effect to trigger a save if one was pending when isSaving becomes false
  useEffect(() => {
    if (!isSaving && pendingSave.current) {
      pendingSave.current = false;
      debouncedSave();
    }
  }, [isSaving, debouncedSave]);

  return (
    <>
      <Accordion
        type="multiple"
        className="w-full"
        defaultValue={sections.map(({ step }) => step)}
        data-testid="review-accordion"
      >
        {sections.map(({ step, title, cssClass }) => (
          <AccordionItem
            value={step}
            key={step}
            className={cn('border-b-0', cssClass, cssClass && 'border-t-[3px]')}
            data-testid={`review-section-${step}`}
          >
            <AccordionTrigger data-testid={`review-${step}`}>
              <Text
                size="lg"
                className="font-bold"
                data-testid={`review-title-${step}`}
              >
                {title}
              </Text>
            </AccordionTrigger>
            <AccordionContent data-testid={`review-content-${step}`}>
              <div
                className="relative"
                data-testid={`review-content-container-${step}`}
              >
                {step === 'getting-started' && (
                  <SourceSelectionReview dataSource={datasource} />
                )}
                {step === 'bronze' && (
                  <BronzeReview
                    dataSource={datasource}
                    datasourceUpgrade={upgradeDiff}
                  />
                )}
                {step === 'silver-pre-transform' && (
                  <PreTransformReview
                    dataSource={datasource}
                    presetVersion={presetUpgradeVersion}
                  />
                )}
                {step === 'silver' && (
                  <SilverReview
                    dataSource={datasource}
                    datasourceUpgrade={upgradeDiff}
                    presetVersion={presetUpgradeVersion}
                  />
                )}
                {step === 'gold' && (
                  <GoldReview
                    dataSource={datasource}
                    presetVersion={presetUpgradeVersion}
                  />
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <DatasourceUpgradeDrawer
        onClose={() => setUnsavedChangesDialogOpen(true)}
        setDrawerHeight={setDrawerHeight}
      />
      <UnsavedChangesDialog
        open={unsavedChangesDialogOpen}
        onConfirm={handleOnConfirm}
        onCancel={() => setUnsavedChangesDialogOpen(false)}
        onDiscard={handleOnDiscard}
        title="Your progress has been saved. Do you want to exit?"
        description="Upon exiting, you will not be able to make edits to this datasource while this upgrade is in progress."
        saveLabel="Yes, exit"
        discardLabel="Abort upgrade"
        cancelLabel="Continue upgrade"
      />
    </>
  );
};
